import {
  getAlbumCustomPrintCartItem,
  getAlbumFolderSubscriptionCartItem,
  getAlbumPrintPackageCartItem,
  getPurchasedPrintPackageCartItem,
  getAlbumSubscriptionCartItem,
  getDigitalDownloadCartItem,
  getIndividualPrintCartItem,
  getLegacyPrintCartItem,
  getPhotoDownloadCartItem,
  getPhotoPricingPrintProduct,
  getPrintProductCartItem,
  getPurchasedPhotoPricingPrintProductCartItem,
  getPurchasedDigitalDownloadCartItem,
  getPurchasedCustomPrintCartItem,
  getDigitalBundleCartItem,
  getPrintPartnerOrderServiceCartItem,
} from './helpers';
import CART_ITEM from '../constants/cartItems';
import uuidv4 from '../../../util/uuid';
import { isAlbum } from '../../../util/album';
import { requestConfirm } from '../../../actions/modal-actions';
import { DIGITAL_DOWNLOADS_MODE } from '../../../constants/print-packages';
import { getAlbumMembershipGroupEntityAccount } from '../../../apps/temp/views/memberships/AlbumMembershipGroup/helpers';

//
// Action Types
//

export const SET_ACTIVE_IDENTITY = 'cart/SET_ACTIVE_IDENTITY';
export const ADD_TO_CART = 'cart/ADD_TO_CART';
export const REMOVE_FROM_CART = 'cart/REMOVE_FROM_CART';
export const CART_ITEM_UPDATE = 'cart/CART_ITEM_UPDATE';
export const CLEAR_CART = 'cart/CLEAR_CART';

//
// Action Creators
//

// General actions

export const setActiveIdentity = identity => ({
  type: SET_ACTIVE_IDENTITY,
  identity,
});

// Cart actions

export const clearCart = () => {
  return {
    type: CLEAR_CART,
  };
};

/**
 * Adds an item to the cart.
 *
 * @typedef {Object<any>} PendingCartItem
 * @property {CART_ITEM} type
 * @property {Object} identity The Identity.
 * @property {Object} [albumFolder] The AlbumFolder if type === ALBUM_FOLDER_SUBSCRIPTION.
 * @property {Object} [album] The Album if ALBUM_SUBSCRIPTION|DIGITAL_DOWNLOAD|INDIVIDUAL_PRINT.
 * @property {Object} [albumCustomPrint] The AlbumCustomPrint if type === ALBUM_CUSTOM_PRINT.
 * @property {Object} [albumPrintPackage] The AlbumPrintPackage if type === ALBUM_PRINT_PACKAGE|ALBUM_CUSTOM_PRINT.
 * @property {Object} [accountCard] The AccountCard if type === PRINT_PRODUCT.
 * @property {Object} [photo] The Photo if type === ALBUM_PRINT_PACKAGE.
 * @property {Object} [photoPricing] The PhotoPricing if type === DIGITAL_DOWNLOAD.
 * @property {boolean} [shipping] Whether an AlbumCustomPrint should be shipped if type === ALBUM_CUSTOM_PRINT.
 * @property {boolean} [subscribed] Whether the identity has purchased a subscription.
 * @property {Object} [photoPrintSize] The print size if type === LEGACY_PRINT.
 *
 * @param {PendingCartItem} item The cart item options.
 * @returns {{item: {identity: *, id, type: *}, type: string}}
 */
export const addToCart = item => {
  const { type, identity, ...itemData } = item;

  return {
    type: ADD_TO_CART,
    // TODO [cart]: run prop validation here with "yup".
    item: {
      id: uuidv4(),
      type,
      identity,
      ...itemData,
    },
  };
};

/**
 * Removes an item from the cart.
 *
 * Note: This action is not exported as it's meant to be used only inside
 * the store's context (i.e. This file only).
 * For uses from the outside (components) use `onCartItemRemove`.
 *
 * @param {string} id The item's id.
 * @returns {{id: string, type: string}}
 */
const removeFromCart = id => {
  return {
    type: REMOVE_FROM_CART,
    id,
  };
};

/**
 * Updates a cart item.
 *
 * @param {string} id The item's id.
 * @param {Object} data The new cart item data.
 * @returns {{data: *, id: *, type: string}}
 */
export const cartItemUpdate = (id, data) => {
  return {
    type: CART_ITEM_UPDATE,
    id,
    data,
  };
};

//
// Actions (thunks, etc)
//

/**
 * Handles PrintPackage cart item removal.
 *
 * @param {Object} item The PrintPackage cart item.
 * @returns {Function}
 */
const handlePrintPackageCartItemRemove = item => (dispatch, getState) => {
  const { cart } = getState().checkout;

  // Remove DigitalDownload and IndividualPrint cart items that require a print
  // package purchase if we are removing the last print package of that particular
  // photo from the cart.
  const photoPrintPackages = cart.filter(
    cartItem =>
      cartItem.type === CART_ITEM.ALBUM_PRINT_PACKAGE && cartItem !== item,
  );
  const photoDigitalDownloads = cart.filter(
    cartItem =>
      cartItem.type === CART_ITEM.DIGITAL_DOWNLOAD &&
      cartItem.photoPricing?.requires_package_purchase,
  );
  const photoIndividualPrints = cart.filter(
    cartItem =>
      cartItem.type === CART_ITEM.INDIVIDUAL_PRINT &&
      cartItem.photoPricing?.requires_package_purchase,
  );
  const photoPricingPrintProducts = cart.filter(
    cartItem =>
      cartItem.type === CART_ITEM.PHOTO_PRICING_PRINT_PRODUCT &&
      cartItem.photoPricing?.requires_package_purchase,
  );
  const albumCustomPrints = cart.filter(
    cartItem =>
      cartItem.type === CART_ITEM.ALBUM_CUSTOM_PRINT &&
      (cartItem.photoPricing?.requires_package_purchase ||
        cartItem.albumCustomPrint?.custom_print?.requires_package_purchase),
  );

  const digitalBundles = cart.filter(
    cartItem =>
      cartItem.type === CART_ITEM.DIGITAL_BUNDLE &&
      cartItem.digitalBundle?.requires_package_purchase,
  );

  if (
    !photoPrintPackages.length &&
    (photoDigitalDownloads.length > 0 ||
      photoIndividualPrints.length > 0 ||
      photoPricingPrintProducts.length > 0 ||
      albumCustomPrints.length > 0 ||
      digitalBundles.length > 0)
  ) {
    dispatch(
      requestConfirm({
        message:
          'Are you sure you want to remove this print package? The hi-res digital download, digital bundles, all individual prints and custom prints for this photo will also be removed.',
        onConfirm: () => {
          [
            ...photoDigitalDownloads,
            ...photoIndividualPrints,
            ...photoPricingPrintProducts,
            ...albumCustomPrints,
            ...digitalBundles,
          ].forEach(cartItem => dispatch(removeFromCart(cartItem.id)));

          dispatch(removeFromCart(item.id));
        },
        onCancel: () => {},
        confirmText: 'Remove',
        cancelText: 'Cancel',
        confirmBtnStyle: 'danger',
      }),
    );

    return;
  }

  dispatch(removeFromCart(item.id));
};

/**
 * Handles removing digital bundles and individual downloads for a given identity
 * when a subscription is added to the cart.
 *
 * @param {Object} options The options.
 * @param {string} options.identityId The identity ID.
 * @returns {Function}
 */
const handleSubscriptionCartItemAdd =
  ({ identityId }) =>
  (dispatch, getState) => {
    if (!identityId) {
      return;
    }

    const { cart } = getState().checkout;

    const identityDigitalBundles = cart.filter(
      cartItem =>
        cartItem.type === CART_ITEM.DIGITAL_BUNDLE &&
        cartItem.identity?.id === identityId,
    );

    const identityDigitalDownloads = cart.filter(
      cartItem =>
        cartItem.type === CART_ITEM.DIGITAL_DOWNLOAD &&
        cartItem.identity?.id === identityId,
    );

    [...identityDigitalBundles, ...identityDigitalDownloads].forEach(cartItem =>
      dispatch(removeFromCart(cartItem.id)),
    );
  };

/**
 * Removes an item from the cart.
 *
 * @param {string} id The cart item id.
 * @returns {function(*, *): *}
 */
export const onCartItemRemove = id => (dispatch, getState) => {
  const { cart } = getState().checkout;
  const item = cart.find(cartItem => cartItem.id === id);

  if (item.type === CART_ITEM.ALBUM_PRINT_PACKAGE) {
    dispatch(handlePrintPackageCartItemRemove(item));
    return;
  }

  dispatch(removeFromCart(id));
};

/**
 * Toggles an AlbumFolder subscription in the cart (adds it if it does not exist
 * or removes if it does).
 *
 * @param {Object} options The action options.
 * @param {Object} options.albumFolder The AlbumFolder.
 * @param {Object} [options.identity] The identity. If not supplied the activeIdentity will be used instead.
 * @param {String} options.hierarchyItemId The hierarchy item id
 * @param {Boolean} options.isGreenScreenEnabled Whether this package was added based off a Green Screen gallery
 * @param {Object} [options.albumMembershipGroup] The albumMembershipGroup
 * @returns {Function}
 */
export const toggleAlbumFolderSubscriptionCartItem =
  ({
    albumFolder,
    identity,
    hierarchyItemId,
    isGreenScreenEnabled,
    albumMembershipGroup,
  }) =>
  (dispatch, getState) => {
    const { cart, activeIdentity } = getState().checkout;
    const item = getAlbumFolderSubscriptionCartItem(cart, {
      albumFolderId: albumFolder.id,
      identityId: identity?.id || activeIdentity.id,
    });

    if (item) {
      dispatch(onCartItemRemove(item.id));
    } else {
      dispatch(
        handleSubscriptionCartItemAdd({
          identityId: identity?.id || activeIdentity.id,
        }),
      );
      dispatch(
        addToCart({
          type: CART_ITEM.ALBUM_FOLDER_SUBSCRIPTION,
          identity: identity || activeIdentity,
          albumFolder,
          hierarchyItemId,
          isGreenScreenEnabled,
          albumMembershipGroup,
          vendor: albumFolder?.account,
        }),
      );
    }
  };

/**
 * Toggles an Album subscription in the cart (adds it if it does not exist
 * or removes if it does).
 *
 * @param {Object} options The action options.
 * @param {Object} options.album The Album.
 * @param {Object} [options.identity] The identity. If not supplied the activeIdentity will be used instead.
 * @param {String} options.hierarchyItemId The hierarchy item id
 * @param {Boolean} options.isGreenScreenEnabled Whether this package was added based off a Green Screen gallery
 * @param {Object} [options.albumMembershipGroup] The albumMembershipGroup
 * @returns {Function}
 */
export const toggleAlbumSubscriptionCartItem =
  ({
    album,
    identity,
    hierarchyItemId,
    isGreenScreenEnabled,
    albumMembershipGroup,
  }) =>
  (dispatch, getState) => {
    const { cart, activeIdentity } = getState().checkout;
    const item = getAlbumSubscriptionCartItem(cart, {
      albumId: album.id,
      identityId: identity?.id || activeIdentity.id,
    });

    if (item) {
      dispatch(onCartItemRemove(item.id));
    } else {
      dispatch(
        handleSubscriptionCartItemAdd({
          identityId: identity?.id || activeIdentity.id,
        }),
      );
      dispatch(
        addToCart({
          type: CART_ITEM.ALBUM_SUBSCRIPTION,
          identity: identity || activeIdentity,
          album,
          hierarchyItemId,
          isGreenScreenEnabled,
          albumMembershipGroup,
          vendor: album?.creator ?? album?.account,
        }),
      );
    }
  };

/**
 * Toggles an AlbumCustomPrint in the cart (adds it if it does not exist
 * or removes if it does).
 *
 * @param {Object} options The action options.
 * @param {Object} options.albumCustomPrint The AlbumCustomPrint.
 * @param {Object} options.albumFolder The albumFolder.
 * @param {Object} [options.identity] The identity. If not supplied the activeIdentity will be used instead.
 * @param {Object} [options.albumMembershipGroup] An album membership group which would limit the selectable photos scope.
 * @param {boolean} options.subscribed Whether the identity has purchased a subscription.
 * @param {boolean} options.shipping Whether this AlbumCustomPrint should be shipped.
 * @param {boolean} [options.configuration] The AlbumCustomPrint's configuration.
 * @param {Object} [options.photo] The photo object. Relevant for the print package limitation basically..
 * @param {Object} [options.photoPricing] The relevant photo pricing configuration.
 * @param {Boolean} [options.isAllPhotos] Whether this package was added based off All Photos or not
 * @param {Boolean} [options.forceEntitlement] Whether this custom print should force entitlement or not
 * @param {Boolean} [options.prePaid] Whether this custom print should force entitlement or not
 * @param {Array} [options.teamPhotos] The team photos for the given identity
 * @returns {Function}
 */
export const toggleAlbumCustomPrintCartItem =
  ({
    albumCustomPrint,
    identity,
    albumFolder,
    albumMembershipGroup,
    subscribed,
    shipping,
    configuration,
    photo,
    photoPricing,
    isAllPhotos = false,
    forceEntitlement = false,
    prePaid = false,
    teamPhotos = [],
  }) =>
  (dispatch, getState) => {
    const { cart, activeIdentity } = getState().checkout;
    const item = getAlbumCustomPrintCartItem(cart, {
      albumCustomPrintId: albumCustomPrint.id,
      identityId: identity?.id || activeIdentity?.id,
      accountCardId: configuration?.accountCard?.id,
    });
    const account = getAlbumMembershipGroupEntityAccount(albumMembershipGroup);

    if (item) {
      dispatch(onCartItemRemove(item.id));
    } else {
      dispatch(
        addToCart({
          type: CART_ITEM.ALBUM_CUSTOM_PRINT,
          identity: identity || activeIdentity,
          albumFolder,
          albumMembershipGroup,
          albumCustomPrint,
          identityIsSubscribed: subscribed,
          shipping,
          quantity: 1,
          configuration,
          photo,
          photoPricing,
          isAllPhotos,
          forceEntitlement,
          vendor:
            photoPricing?.account ??
            albumCustomPrint?.custom_print?.account ??
            account,
          prePaid,
          teamPhotos,
        }),
      );
    }
  };

/**
 * Toggles an PurchasedCustomPrint in the cart (adds it if it does not exist
 * or removes if it does).
 *
 * @param {Object} options The action options.
 * @param {Object} options.purchasedCustomPrint The PurchasedCustomPrint.
 * @param {Object} [options.identity] The identity. If not supplied the activeIdentity will be used instead.
 * @param {Number} [options.quantity] The amount of items of the given type to add
 * @param {Object} [options.albumMembershipGroup] An album membership group which would limit the selectable photos scope.
 * @param {boolean} options.shipping Whether this PurchasedCustomPrint should be shipped.
 * @param {boolean} [options.configuration] The AlbumCustomPrint's configuration.
 * @param {Boolean} [options.isAllPhotos] Whether this package was added based off All Photos or not
 * @returns {Function}
 */
export const togglePurchasedCustomPrintCartItem =
  ({
    purchasedCustomPrint,
    identity,
    quantity = 1,
    albumMembershipGroup,
    shipping,
    configuration,
    isAllPhotos = false,
  }) =>
  (dispatch, getState) => {
    const { cart, activeIdentity } = getState().checkout;
    const item = getPurchasedCustomPrintCartItem(cart, {
      purchasedCustomPrintId: purchasedCustomPrint.id,
    });
    const { album_custom_print: albumCustomPrint } = purchasedCustomPrint;
    const account = getAlbumMembershipGroupEntityAccount(albumMembershipGroup);

    if (item) {
      dispatch(onCartItemRemove(item.id));
    } else {
      dispatch(
        addToCart({
          type: CART_ITEM.PURCHASED_CUSTOM_PRINT,
          identity: identity || activeIdentity,
          albumMembershipGroup,
          purchasedCustomPrint,
          shipping,
          quantity,
          configuration,
          isAllPhotos,
          vendor: albumCustomPrint?.custom_print?.account ?? account,
        }),
      );
    }
  };

/**
 * Toggles an Album or AlbumFolder Subscription and an AlbumCustomPrint in the cart
 * at the same time (simulating a "bundle").
 *
 * @param {Object} options The action options.
 * @param {Object} options.albumCustomPrint The AlbumCustomPrint.
 * @param {Object} options.subscriptionItem The AlbumFolder or Album containing a subscription.
 * @param {Object} [options.identity] The identity. If not supplied the activeIdentity will be used instead.
 * @param {Object} [options.albumMembershipGroup] An album membership group which would limit the selectable photos scope.
 * @param {boolean} options.shipping Whether the AlbumCustomPrint should be shipped.
 * @param {boolean} [options.configuration] The AlbumCustomPrint's configuration.
 * @returns {Function}
 */
export const toggleAlbumCustomPrintSubscriptionBundle =
  ({
    albumCustomPrint,
    subscriptionItem,
    identity,
    albumMembershipGroup,
    shipping,
    configuration,
  }) =>
  (dispatch, getState) => {
    const { cart, activeIdentity } = getState().checkout;
    const identityId = identity?.id || activeIdentity.id;

    const albumSubscriptionCartItem = getAlbumSubscriptionCartItem(cart, {
      albumId: subscriptionItem.id,
      identityId,
    });
    const albumFolderSubscriptionCartItem = getAlbumFolderSubscriptionCartItem(
      cart,
      {
        albumFolderId: subscriptionItem.id,
        identityId,
      },
    );
    const subscriptionCartItem =
      albumFolderSubscriptionCartItem || albumSubscriptionCartItem;
    const albumCustomPrintCartItem = getAlbumCustomPrintCartItem(cart, {
      albumCustomPrintId: albumCustomPrint.id,
      identityId,
      accountCardId: configuration?.accountCard?.id,
    });

    const account = getAlbumMembershipGroupEntityAccount(albumMembershipGroup);

    if (subscriptionCartItem && albumCustomPrintCartItem) {
      dispatch(onCartItemRemove(subscriptionCartItem.id));
      dispatch(onCartItemRemove(albumCustomPrintCartItem.id));
    } else {
      const isAlbumSubscription = isAlbum(subscriptionItem);
      dispatch(
        handleSubscriptionCartItemAdd({
          identityId: identity?.id || activeIdentity.id,
        }),
      );
      dispatch(
        addToCart({
          type: isAlbumSubscription
            ? CART_ITEM.ALBUM_SUBSCRIPTION
            : CART_ITEM.ALBUM_FOLDER_SUBSCRIPTION,
          identity: identity || activeIdentity,
          [isAlbumSubscription ? 'album' : 'albumFolder']: subscriptionItem,
          albumMembershipGroup,
          vendor: albumCustomPrint?.custom_print?.account ?? account,
        }),
      );

      dispatch(
        addToCart({
          type: CART_ITEM.ALBUM_CUSTOM_PRINT,
          identity: identity || activeIdentity,
          albumMembershipGroup,
          albumCustomPrint,
          identityIsSubscribed: false,
          shipping,
          configuration,
          vendor: albumCustomPrint?.custom_print?.account ?? account,
        }),
      );
    }
  };

/**
 * Toggles an AlbumPrintPackage in the cart (adds it if it does not exist
 * or removes if it does).
 *
 * @typedef {Object} ExtendedPrintPackageItem
 * @property {String} printPackageItem The PrintPackageItem id.
 * @property {Object} photo The photo.
 *
 * @param {Object} options The action options.
 * @param {Object} options.albumPrintPackage The AlbumPrintPackage.
 * @param {Object} options.photo The Photo.
 * @param {Object} options.album The Album the photo belongs to.
 * @param {Object} [options.identity] The identity. If not supplied the activeIdentity will be used instead.
 * @param {boolean} [options.includeDigitalDownload] Whether a digital download should be included as part of the purchase.
 * @param {Array<ExtendedPrintPackageItem>} [options.extendedPrintPackageItems] Any optional PrintPackageItems.
 * @param {Object} [options.albumMembershipGroup] An album membership group which would limit the selectable photos scope.
 * @param {Boolean} [options.prePaid] Whether the item is to be prePaid or not
 * @param {Boolean} [options.allowMultiPose] Whether multi pose is allowed for this item
 * @param {Array} [options.teamPhotos] The team photos for the given identity
 * @param {Boolean} [options.isAllPhotos] Whether this package was added based off All Photos or not
 * @param {String} [options.hierarchyItemId] The hierarchy item id
 * @param {Boolean} [options.isGreenScreenEnabled] Whether this package was added based off a Green Screen gallery
 * @param {Boolean} [options.selectedPrintPackageItems] The selected items for max_checkout_items configs
 * @returns {Function}
 */
export const toggleAlbumPrintPackageCartItem =
  ({
    albumPrintPackage,
    photo,
    album,
    identity,
    includeDigitalDownload = false,
    extendedPrintPackageItems = [],
    albumMembershipGroup,
    prePaid = false,
    isAllPhotos = false,
    hierarchyItemId = '',
    isGreenScreenEnabled = false,
    selectedPrintPackageItems = [],
    allowMultiPose = false,
    teamPhotos = [],
  }) =>
  (dispatch, getState) => {
    const { cart, activeIdentity } = getState().checkout;
    const item = getAlbumPrintPackageCartItem(cart, {
      albumPrintPackageId: albumPrintPackage.id,
      photoId: photo?.id,
      identityId: identity?.id,
    });

    const account = getAlbumMembershipGroupEntityAccount(albumMembershipGroup);

    if (item) {
      dispatch(onCartItemRemove(item.id));
    } else {
      dispatch(
        addToCart({
          type: CART_ITEM.ALBUM_PRINT_PACKAGE,
          identity: identity || activeIdentity,
          albumPrintPackage,
          photo,
          album,
          includeDigitalDownload,
          extendedPrintPackageItems,
          albumMembershipGroup,
          prePaid,
          isAllPhotos,
          hierarchyItemId,
          isGreenScreenEnabled,
          selectedPrintPackageItems,
          allowMultiPose,
          quantity: 1,
          vendor:
            albumPrintPackage?.account ??
            albumPrintPackage?.print_package?.account ??
            account,
          teamPhotos,
        }),
      );
      const packageHasDownloads =
        albumPrintPackage.print_package?.digital_downloads_mode ===
        DIGITAL_DOWNLOADS_MODE.INCLUDED;

      if (photo && !isGreenScreenEnabled) {
        const digitalDownloadItem = getDigitalDownloadCartItem(cart, {
          photoId: photo.id,
          albumId: album?.id,
        });
        const photoDownloadItem = getPhotoDownloadCartItem(cart, {
          photoId: photo.id,
        });

        // If the download is included in the added package, remove it from the cart
        if (digitalDownloadItem && packageHasDownloads) {
          dispatch(onCartItemRemove(digitalDownloadItem.id));
        }
        if (photoDownloadItem && packageHasDownloads) {
          dispatch(onCartItemRemove(photoDownloadItem.id));
        }
      }
    }
  };

/**
 * Toggles a PurchasedPrintPackage in the cart (adds it if it does not exist
 * or removes if it does).
 *
 * @param {Object} options The action options.
 * @param {Object} options.albumPrintPackage The AlbumPrintPackage.
 * @param {Object} options.purchasedPrintPackage The PurchasedPrintPackage.
 * @param {Object} options.photo The Photo.
 * @param {Object} options.album The Album the photo belongs to.
 * @param {Object} [options.identity] The identity. If not supplied the activeIdentity will be used instead.
 * @param {Object} [options.includeDigitalDownload] Whether a digital download should be included as part of the purchase.
 * @param {Object} [options.albumMembershipGroup] An album membership group which would limit the selectable photos scope.
 * @param {Number} [options.quantity] The amount of items of the given type to add
 * @param {Boolean} [options.isAllPhotos] Whether this package was added based off All Photos or not
 * @param {String} [options.hierarchyItemId] The hierarchy item id
 * @param {Boolean} [options.isGreenScreenEnabled] Whether this package was added based off a Green Screen gallery
 * @param {Boolean} [options.selectedPrintPackageItems] The selected items for max_checkout_items configs
 * @param {Boolean} [options.allowMultiPose] Whether multi pose is allowed for this item
 * @returns {Function}
 */
export const togglePurchasedPrintPackageCartItem =
  ({
    albumPrintPackage,
    purchasedPrintPackage,
    photo,
    album,
    identity,
    includeDigitalDownload = false,
    albumMembershipGroup,
    quantity = 1,
    isAllPhotos = false,
    hierarchyItemId = '',
    isGreenScreenEnabled = false,
    selectedPrintPackageItems = [],
    allowMultiPose = false,
  }) =>
  (dispatch, getState) => {
    const { cart } = getState().checkout;
    const item = getPurchasedPrintPackageCartItem(cart, {
      purchasedPrintPackageId: purchasedPrintPackage.id,
      photoId: photo?.id,
      identityId: identity?.id,
    });

    const account = getAlbumMembershipGroupEntityAccount(albumMembershipGroup);

    if (item) {
      dispatch(onCartItemRemove(item.id));
    } else {
      dispatch(
        addToCart({
          type: CART_ITEM.PURCHASED_PRINT_PACKAGE,
          identity,
          albumPrintPackage,
          purchasedPrintPackage,
          photo,
          album,
          includeDigitalDownload,
          albumMembershipGroup,
          isAllPhotos,
          hierarchyItemId,
          isGreenScreenEnabled,
          allowMultiPose,
          quantity,
          vendor:
            albumPrintPackage?.account ??
            albumPrintPackage?.print_package?.account ??
            account,
          selectedPrintPackageItems,
        }),
      );
    }
  };

/**
 * Toggles an Album or AlbumFolder Subscription and an AlbumPrintPackage in the cart
 * at the same time (simulating a "bundle").
 *
 * @param {Object} options The action options.
 * @param {Object} options.albumPrintPackage The AlbumPrintPackage.
 * @param {Object} options.photo The AlbumPrintPackage photo.
 * @param {Object} [options.album] The album the AlbumPrintPackage belongs to.
 * @param {Object} [options.includeDigitalDownload] Whether to include the hi-res digital download with the AlbumPrintPackage.
 * @param {Object} options.subscriptionItem The AlbumFolder or Album containing a subscription.
 * @param {Object} [options.identity] The identity. If not supplied the activeIdentity will be used instead.
 * @param {Object} [options.albumMembershipGroup] The albumMembershipGroup
 * @returns {Function}
 */
export const toggleAlbumPrintPackageSubscriptionBundle =
  ({
    albumPrintPackage,
    photo,
    album,
    includeDigitalDownload = false,
    subscriptionItem,
    identity,
    albumMembershipGroup,
  }) =>
  (dispatch, getState) => {
    const { cart, activeIdentity } = getState().checkout;
    const identityId = identity?.id || activeIdentity.id;

    const albumSubscriptionCartItem = getAlbumSubscriptionCartItem(cart, {
      albumId: subscriptionItem.id,
      identityId,
    });
    const albumFolderSubscriptionCartItem = getAlbumFolderSubscriptionCartItem(
      cart,
      {
        albumFolderId: subscriptionItem.id,
        identityId,
      },
    );
    const subscriptionCartItem =
      albumFolderSubscriptionCartItem || albumSubscriptionCartItem;
    const albumPrintPackageCartItem = getAlbumPrintPackageCartItem(cart, {
      albumPrintPackageId: albumPrintPackage.id,
      photoId: photo?.id,
    });

    if (subscriptionCartItem && albumPrintPackageCartItem) {
      dispatch(onCartItemRemove(subscriptionCartItem.id));
      dispatch(onCartItemRemove(albumPrintPackageCartItem.id));
    } else {
      const isAlbumSubscription = isAlbum(subscriptionItem);

      dispatch(
        addToCart({
          type: isAlbumSubscription
            ? CART_ITEM.ALBUM_SUBSCRIPTION
            : CART_ITEM.ALBUM_FOLDER_SUBSCRIPTION,
          identity: identity || activeIdentity,
          [isAlbumSubscription ? 'album' : 'albumFolder']: subscriptionItem,
          albumMembershipGroup,
          vendor: albumPrintPackage?.account,
        }),
      );

      dispatch(
        addToCart({
          type: CART_ITEM.ALBUM_PRINT_PACKAGE,
          identity: identity || activeIdentity,
          albumPrintPackage,
          photo,
          album,
          includeDigitalDownload,
          albumMembershipGroup,
          quantity: 1,
          vendor: albumPrintPackage?.account,
        }),
      );
    }
  };

/**
 * Toggles a digital download in the cart (adds it if it does not exist
 * or removes if it does).
 *
 * @param {Object} options The action options.
 * @param {Object} options.photo The Photo.
 * @param {Object} options.album The Album the photo belongs to.
 * @param {Object} options.albumFolder The albumFolder the photo belongs to.
 * @param {Object} options.photoPricing The PhotoPricing.
 * @param {String} options.hierarchyItemId the hierarchy item id
 * @param {Boolean} options.isGreenScreenEnabled if Green Screen is enabled for this AMG
 * @param {Boolean} options.prePaid Whether or not this item is pre-paid
 * @param {Object} [options.identity] The identity. If not supplied the activeIdentity will be used instead.
 * @param {Object} [options.albumMembershipGroup] The albumMembershipGroup
 * @returns {Function}
 */
export const toggleDigitalDownloadCartItem =
  ({
    photo,
    identity,
    album,
    albumFolder,
    photoPricing,
    hierarchyItemId,
    isGreenScreenEnabled,
    prePaid = false,
    albumMembershipGroup,
  }) =>
  (dispatch, getState) => {
    const { cart, activeIdentity } = getState().checkout;
    const item = getDigitalDownloadCartItem(cart, {
      photoId: photo?.id,
      identityId: identity?.id ?? activeIdentity?.id,
      prePaid,
    });

    if (item) {
      dispatch(onCartItemRemove(item.id));
    } else {
      dispatch(
        addToCart({
          type: CART_ITEM.DIGITAL_DOWNLOAD,
          identity: identity || activeIdentity,
          photo,
          album,
          albumFolder,
          photoPricing,
          hierarchyItemId,
          isGreenScreenEnabled,
          prePaid,
          quantity: prePaid ? 1 : undefined,
          vendor: photoPricing?.account,
          albumMembershipGroup,
        }),
      );
    }
  };

/**
 * Toggles a purchased digital download in the cart (adds it if it does not exist
 * or removes if it does).
 *
 * @param {Object} options The action options.
 * @param {Object} options.photo The Photo.
 * @param {Object} options.album The Album the photo belongs to.
 * @param {Object} options.photoPricing The PhotoPricing.
 * @param {String} options.hierarchyItemId the hierarchy item id
 * @param {Boolean} options.isGreenScreenEnabled if Green Screen is enabled for this AMG
 * @param {Object} options.purchasedPhotoPricing The PurchasedPhotoPricing.
 * @param {Object} [options.identity] The identity. If not supplied the activeIdentity will be used instead.
 * @param {Object} [options.albumMembershipGroup] The albumMembershipGroup
 * @returns {Function}
 */
export const togglePurchasedDigitalDownloadCartItem =
  ({
    photo,
    identity,
    album,
    photoPricing,
    hierarchyItemId,
    isGreenScreenEnabled,
    purchasedPhotoPricing,
    albumMembershipGroup,
  }) =>
  (dispatch, getState) => {
    const { cart, activeIdentity } = getState().checkout;
    const item = getPurchasedDigitalDownloadCartItem(cart, {
      purchasedPhotoPricingId: purchasedPhotoPricing.id,
      photoId: photo?.id,
    });

    if (item) {
      dispatch(onCartItemRemove(item.id));
    } else {
      dispatch(
        addToCart({
          type: CART_ITEM.PURCHASED_DIGITAL_DOWNLOAD,
          identity: identity || activeIdentity,
          photo,
          album,
          photoPricing,
          hierarchyItemId,
          isGreenScreenEnabled,
          purchasedPhotoPricing,
          albumMembershipGroup,
          vendor: photoPricing?.account,
        }),
      );
    }
  };

/**
 * Toggles an individual print in the cart (adds it if it does not exist
 * or removes if it does).
 *
 * @param {Object} options The action options.
 * @param {Object} options.photo The Photo.
 * @param {Object} options.album The Album the photo belongs to.
 * @param {Object} options.printItem The PrintItem.
 * @param {Object} options.photoPricing The PhotoPricing.
 * @param {Object} [options.identity] The identity. If not supplied the activeIdentity will be used instead.
 * @param {boolean} [options.watermark] If the print should be watermarked (brand filter).
 * @param {Object} [options.albumMembershipGroup] The albumMembershipGroup
 * @param {Array} [options.teamPhotos] The team photos for the given identity
 * @returns {Function}
 */
export const toggleIndividualPrintCartItem =
  ({
    photo,
    identity,
    album,
    printItem,
    photoPricing,
    watermark = false,
    albumMembershipGroup,
    teamPhotos = [],
  }) =>
  (dispatch, getState) => {
    const { cart, activeIdentity } = getState().checkout;
    const item = getIndividualPrintCartItem(cart, {
      photoId: photo.id,
      printItemId: printItem.id,
    });

    if (item) {
      dispatch(onCartItemRemove(item.id));
    } else {
      dispatch(
        addToCart({
          type: CART_ITEM.INDIVIDUAL_PRINT,
          identity: identity || activeIdentity,
          photo,
          album,
          printItem,
          photoPricing,
          quantity: 1,
          watermark,
          albumMembershipGroup,
          vendor: photoPricing?.account,
          teamPhotos,
        }),
      );
    }
  };

/**
 * Toggles a legacy print in the cart (adds it if it does not exist
 * or removes if it does).
 *
 * @param {Object} options The action options.
 * @param {Object} options.photo The Photo.
 * @param {Object} [options.album] The Album the photo belongs to.
 * @param {Object} options.photoPrintSize The PhotoPrintSize.
 * @param {Object} [options.identity] The identity. If not supplied the activeIdentity will be used instead.
 * @param {boolean} [options.watermark] If the print should be watermarked (brand filter).
 * @param {Object} [options.albumMembershipGroup] The albumMembershipGroup
 * @returns {Function}
 */
export const toggleLegacyPrintCartItem =
  ({
    photo,
    identity,
    album,
    photoPrintSize,
    watermark = false,
    albumMembershipGroup,
  }) =>
  (dispatch, getState) => {
    const { cart, activeIdentity } = getState().checkout;
    const item = getLegacyPrintCartItem(cart, {
      photoId: photo.id,
      photoPrintSizeId: photoPrintSize.id,
    });

    if (item) {
      dispatch(onCartItemRemove(item.id));
    } else {
      dispatch(
        addToCart({
          type: CART_ITEM.LEGACY_PRINT,
          identity: identity || activeIdentity,
          photo,
          album,
          photoPrintSize,
          quantity: 1,
          watermark,
          albumMembershipGroup,
          vendor: album?.creator ?? album?.account,
        }),
      );
    }
  };

/**
 * Toggles a photo download (FWG) in the cart (adds it if it does not exist
 * or removes if it does).
 *
 * @param {Object} options The action options.
 * @param {Object} options.photo The Photo.
 * @param {Object} [options.identity] The identity. If not supplied the activeIdentity will be used instead.
 * @param {Object} [options.album] The Album the photo belongs to.
 * @param {Object} [options.webGalleryPrice] The Free Web gallery pricing, value & currency
 * @param {Object} [options.albumMembershipGroup] The albumMembershipGroup
 * @returns {Function}
 */
export const togglePhotoDownloadCartItem =
  ({ photo, identity, album, webGalleryPrice, albumMembershipGroup }) =>
  (dispatch, getState) => {
    const { cart, activeIdentity } = getState().checkout;
    const item = getPhotoDownloadCartItem(cart, {
      photoId: photo.id,
      albumId: album.id,
    });

    if (item) {
      dispatch(onCartItemRemove(item.id));
    } else {
      dispatch(
        addToCart({
          type: CART_ITEM.PHOTO_DOWNLOAD,
          identity: identity || activeIdentity,
          photo,
          album,
          webGalleryPrice,
          albumMembershipGroup,
          vendor: album?.creator ?? album?.account,
        }),
      );
    }
  };

/**
 * Toggles a PrintProduct item in the cart (adds it if it does not exist
 * or removes if it does).
 *
 * @param {Object} options The action options.
 * @param {Object} options.accountCard The AccountCard.
 * @param {Object} options.albumCustomPrint The global PrintProduct AlbumCustomPrint.
 * @param {Object} options.printProductSize The PrintProductSize
 * @param {Object} [options.photo] The Photo (optional - does not require it).
 * @param {Object} [options.identity] The identity. If not supplied the activeIdentity will be used instead.
 * @param {Object} [options.albumMembershipGroup] The album Membership Group
 * @param {Array} [options.teamPhotos] The team photos for the given identity
 * @returns {Function}
 */
export const togglePrintProductCartItem =
  ({
    albumCustomPrint,
    printProductSize,
    accountCard,
    photo,
    identity,
    albumMembershipGroup,
    teamPhotos = [],
  }) =>
  (dispatch, getState) => {
    const { cart, activeIdentity } = getState().checkout;
    const item = getPrintProductCartItem(cart, {
      printProductSizeId: printProductSize.id,
      photoId: photo?.id,
    });

    const account = getAlbumMembershipGroupEntityAccount(albumMembershipGroup);

    if (item) {
      dispatch(onCartItemRemove(item.id));
    } else {
      dispatch(
        addToCart({
          type: CART_ITEM.PRINT_PRODUCT,
          identity: identity || activeIdentity,
          photo,
          albumCustomPrint,
          printProductSize,
          accountCard,
          albumMembershipGroup,
          quantity: 1,
          vendor: albumCustomPrint?.custom_print?.account ?? account,
          teamPhotos,
        }),
      );
    }
  };

/**
 * Toggles an photo pricing print product in the cart (adds it if it does not exist
 * or removes if it does).
 *
 * @param {Object} options The action options.
 * @param {Object} options.photo The Photo.
 * @param {Object} options.album The Album.
 * @param {Object} options.albumFolder The Album Folder.
 * @param {Object} options.accountCard The configuration of the card.
 * @param {Object} options.photoPricingPrintProduct The photo pricing print product item.
 * @param {Object} [options.identity] The identity. If not supplied the activeIdentity will be used instead.
 * @param {Object} [options.albumMembershipGroup] The Album Membership Group
 * @param {Object} [options.photoPricing] The relevant photo pricing pbject
 * @param {Object} [options.roesConfiguration] The ROES configuration for ROES configurable print products
 * @param {Boolean} [options.prePaid] whether or not the album is pre-paid
 * @param {Boolean} [options.isAllPhotos] whether or not to use isAllPhotos context
 * @param {Array} [options.teamPhotos] The team photos for the given identity
 * @returns {Function}
 */
export const togglePhotoPricingPrintProductCartItem =
  ({
    accountCard,
    photo,
    identity,
    photoPricingPrintProduct,
    album,
    albumFolder,
    albumMembershipGroup,
    photoPricing,
    roesConfiguration,
    prePaid = false,
    isAllPhotos = false,
    teamPhotos = [],
  }) =>
  (dispatch, getState) => {
    const { cart, activeIdentity } = getState().checkout;
    const item = getPhotoPricingPrintProduct(cart, {
      photoPricingPrintProductId: photoPricingPrintProduct.id,
      photoId: photo?.id,
      identityId: identity?.id ?? activeIdentity?.id,
      prePaid,
    });

    const account = getAlbumMembershipGroupEntityAccount(albumMembershipGroup);

    if (item) {
      dispatch(onCartItemRemove(item.id));
    } else {
      dispatch(
        addToCart({
          type: CART_ITEM.PHOTO_PRICING_PRINT_PRODUCT,
          identity: identity || activeIdentity,
          photo,
          accountCard,
          photoPricingPrintProduct,
          prePaid,
          quantity: 1,
          album,
          albumFolder,
          albumMembershipGroup,
          photoPricing,
          roesConfiguration,
          isAllPhotos,
          vendor: photoPricing?.account ?? account,
          teamPhotos,
        }),
      );
    }
  };

/**
 * Toggles a purchased photo pricing print product in the cart (adds it if it does not exist
 * or removes if it does).
 *
 * @param {Object} options The action options.
 * @param {Object} options.photo The Photo.
 * @param {Object} options.album The Album.
 * @param {Object} options.accountCard The configuration of the card.
 * @param {Object} options.photoPricingPrintProduct The photo pricing print product item.
 * @param {Object} [options.identity] The identity. If not supplied the activeIdentity will be used instead.
 * @param {Object} [options.albumMembershipGroup] The Album Membership Group
 * @param {Object} [options.photoPricing] The relevant photo pricing pbject
 * @param {Object} [options.purchasedPhotoPricing] The relevant purchased photo pricing pbject
 * @param {Object} [options.roesConfiguration] The ROES configuration for ROES configurable print products
 * @param {Object} [options.isAllPhotos] whether or not to use isAllPhotos context
 * @returns {Function}
 */
export const togglePurchasedPhotoPricingPrintProductCartItem =
  ({
    accountCard,
    photo,
    identity,
    photoPricingPrintProduct,
    album,
    albumMembershipGroup,
    photoPricing,
    purchasedPhotoPricing,
    roesConfiguration,
    isAllPhotos = false,
  }) =>
  (dispatch, getState) => {
    const { cart, activeIdentity } = getState().checkout;
    const item = getPurchasedPhotoPricingPrintProductCartItem(cart, {
      purchasedPhotoPricingId: purchasedPhotoPricing.id,
    });

    const account = getAlbumMembershipGroupEntityAccount(albumMembershipGroup);

    if (item) {
      dispatch(onCartItemRemove(item.id));
    } else {
      dispatch(
        addToCart({
          type: CART_ITEM.PURCHASED_PHOTO_PRICING_PRINT_PRODUCT,
          identity: identity || activeIdentity,
          photo,
          accountCard,
          photoPricingPrintProduct,
          quantity: purchasedPhotoPricing?.quantity ?? 1,
          album,
          albumMembershipGroup,
          photoPricing,
          purchasedPhotoPricing,
          roesConfiguration,
          isAllPhotos,
          vendor: photoPricing?.account ?? account,
        }),
      );
    }
  };

/**
 * Toggles a digital photo bundle in the cart (adds it if it does not exist
 * or removes if it does).
 *
 * @param {Object} options The action options.
 * @param {Object} [options.albumMembershipGroup] The Album Membership Group
 * @param {Object} options.digitalBundle The digital bundle.
 * @param {Object} options.photos The selected photos.
 * @param {String} options.hierarchyItemId the hierarchy item id
 * @param {Boolean} options.isGreenScreenEnabled if Green Screen is enabled for this AMG
 * @param {Boolean} options.prePaid Whether or not this item is pre-paid
 * @param {Object} [options.identity] The identity. If not supplied the activeIdentity will be used instead.
 * @returns {Function}
 */
export const toggleDigitalBundleCartItem =
  ({
    albumMembershipGroup,
    identity,
    digitalBundle,
    photos,
    hierarchyItemId,
    isGreenScreenEnabled,
    prePaid = false,
  }) =>
  (dispatch, getState) => {
    const { cart, activeIdentity } = getState().checkout;
    const item = getDigitalBundleCartItem(cart, {
      digitalBundleId: digitalBundle?.id,
      identityId: identity?.id ?? activeIdentity?.id,
      photosCount: digitalBundle?.photos_count,
      photoId: photos?.[0]?.id,
      prePaid,
    });

    if (item) {
      dispatch(onCartItemRemove(item.id));
    } else {
      dispatch(
        addToCart({
          type: CART_ITEM.DIGITAL_BUNDLE,
          albumMembershipGroup,
          identity: identity || activeIdentity,
          photos,
          digitalBundle,
          hierarchyItemId,
          isGreenScreenEnabled,
          prePaid,
          vendor: digitalBundle?.account,
        }),
      );
    }
  };

/**
 * Toggles a print order service in the cart (adds it if it does not exist
 * or removes if it does).
 *
 * @param {Object} options The action options.
 * @param {Object} [options.albumMembershipGroup] The Album Membership Group
 * @param {Object} options.printPartnerOrderService The print partner order service.
 * @returns {Function}
 */
export const togglePrintPartnerOrderServiceCartItem =
  ({ albumMembershipGroup, printPartnerOrderService }) =>
  (dispatch, getState) => {
    const { cart } = getState().checkout;
    const item = getPrintPartnerOrderServiceCartItem(cart, {
      printPartnerOrderServiceId: printPartnerOrderService?.id,
    });

    if (item) {
      dispatch(onCartItemRemove(item.id));
    } else {
      dispatch(
        addToCart({
          type: CART_ITEM.PRINT_ORDER_SERVICE,
          albumMembershipGroup,
          printPartnerOrderService,
        }),
      );
    }
  };

/**
 * Get all cart items for a given identity
 *
 * @param {String} identityId The identity ID.
 * @returns {Function}
 */
export const getIdentityCartItems =
  ({ identityId }) =>
  (dispatch, getState) => {
    if (!identityId) {
      return [];
    }

    const { cart } = getState().checkout;
    return cart.filter(item => item.identity?.id === identityId);
  };

/**
 * Removes all items for a specific identity
 *
 * @param {String} identityId The identity ID.
 * @returns {Function}
 */
export const onIdentityCartItemsRemove =
  ({ identityId }) =>
  (dispatch, getState) => {
    if (!identityId) {
      return;
    }

    const { cart } = getState().checkout;
    const identityItems = cart.filter(item => item.identity?.id === identityId);
    identityItems.forEach(item => dispatch(onCartItemRemove(item.id)));
  };
